import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import { createLiveStore, type LiveState, type LiveStore } from './store.ts';

const StoreContext = createContext<LiveStore | null>(null);

export function LiveProvider({ store, children }: { store: LiveStore; children: ReactNode }) {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

export function useStore(): LiveStore {
  const store = useContext(StoreContext);
  if (!store) throw new Error('useStore must be used inside a LiveProvider');
  return store;
}

export function useLive(): LiveState {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, store.getState, store.getState);
}

/**
 * Subscribe to one slice.
 *
 * The clock ticks once a second, so a component reading whole state re-renders every second
 * whether or not anything it draws changed. That is fine for an agent card (its timer *is*
 * the clock) and wasteful for a settings panel. `useSlice` keeps the cheap components out of
 * the tick.
 *
 * `select` must return the same value for the same state: `?? []` or an object literal is a
 * new value on every read, which never compares equal and re-renders forever. Return a
 * module-level constant for the empty case, and prefer a module-level selector so the
 * cached reader is not rebuilt each render.
 */
export function useSlice<T>(select: (state: LiveState) => T, isEqual?: (a: T, b: T) => boolean): T {
  const store = useStore();
  const equal = isEqual ?? Object.is;
  return useSyncExternalStore(
    store.subscribe,
    useMemo(() => {
      let last: { value: T } | null = null;
      return () => {
        const next = select(store.getState());
        if (last && equal(last.value, next)) return last.value;
        last = { value: next };
        return next;
      };
    }, [store, select, equal]),
  );
}

export function createStore(): LiveStore {
  return createLiveStore();
}
